import React from 'react';

import { BackToTopButton } from 'components/no-auth/common/back-to-top-button/BackToTopButton';
import { BlogTile } from 'components/no-auth/common/blog-tile/BlogTile';
import { Breadcrumbs } from 'components/no-auth/common/breadcrumbs/Breadcrumbs';
import { Date } from 'components/no-auth/common/date/Date';
import { PromoBadge } from 'components/no-auth/common/promo-badge/PromoBadge';
import { PromoDangerouslyHtml } from 'components/no-auth/common/promo-dangerously-html/PromoDangerouslyHtml';
import { PromoPageContainer } from 'components/no-auth/common/promo-page-container/PromoPageContainer';
import { PromoText } from 'components/no-auth/common/promo-text/PromoText';
import { PromoRow } from 'components/no-auth/common/PromoRow/PromoRow';
import { PromoSectionContainer } from 'components/no-auth/common/PromoSectionContainer';
import HeadingSection from 'components/no-auth/common/sections/HeadingSection';
import { QuoteSectionV2 } from 'components/no-auth/common/sections/QuoteSectionV2';
import { TextComponent } from 'components/no-auth/common/sections/styles';
import { TilesSection } from 'components/no-auth/common/sections/TilesSection';
import NewsLayoutV2 from 'components/no-auth/news/NewsLayoutV2';

import { getLangCountryPrefix, resolveArguments } from 'helpers/utils';

import styles from './NewsPage.module.css';

const NewsArticlePageV2 = (props) => {
    const url = `${getLangCountryPrefix(props.lang, props.country)}/${props.stateOptions?.section}`;

    const breadcrumbs = [{ name: props.landing?.allArticle, url: url }, { name: props.selectedArticle?.title }];

    const getReadingTime = (text) => {
        const wordsPerMinute = 200;
        const numberOfWords = text.split(/\s+/).length;
        return Math.ceil(numberOfWords / wordsPerMinute);
    };

    return (
        <PromoPageContainer hasTopPadding>
            {props.selectedArticle && props.landing && props.articles && (
                <PromoSectionContainer>
                    {props.landing.allArticle && props.selectedArticle.title && <Breadcrumbs breadcrumbsItems={breadcrumbs} />}
                    {props.selectedArticle.title && <HeadingSection title={props.selectedArticle.title} titleTag={'h1'} />}
                    <PromoRow flex flexWrap align="center" justify={'between'} indent="adaptive-32-48-64" rowGap="16px">
                        {props.selectedArticle.date && <Date date={props.selectedArticle.date} textVariant="subheader" />}

                        <PromoRow flex flexWrap align="center" rowGap="8px">
                            {Array.isArray(props.selectedArticle.categories) &&
                                props.selectedArticle.categories
                                    .filter((item) => item !== null)
                                    .map((item) => {
                                        return (
                                            <div className={styles.badgeItem} key={item.id}>
                                                <PromoBadge caption={item.title} isInactive />
                                            </div>
                                        );
                                    })}
                        </PromoRow>
                        <div>
                            {props.landing.reading_time_text && props.selectedArticle.description && (
                                <PromoText color={'gray40'} variant={'subheader'}>
                                    {resolveArguments(props.landing.reading_time_text, getReadingTime(props.selectedArticle.description))}
                                </PromoText>
                            )}
                        </div>
                    </PromoRow>

                    {props.selectedArticle.img && (
                        <img
                            className={styles.illustration}
                            src={props.selectedArticle.img?.url}
                            alt={props.selectedArticle.img?.alternativeText}
                            loading={'lazy'}
                            decoding={'async'}
                        />
                    )}

                    {props.selectedArticle.video_url && (
                        <div className={styles.videoContainer}>
                            <div>
                                <iframe
                                    src={props.selectedArticle.video_url}
                                    width="100%"
                                    height="100%"
                                    frameBorder="0"
                                    allow="autoplay; encrypted-media;accelerometer; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen="1"
                                    loading="lazy"
                                />
                            </div>
                        </div>
                    )}

                    {props.selectedArticle.description && (
                        <div className={styles.description}>
                            <TextComponent>
                                <PromoDangerouslyHtml color={'baseColor'} tag={'div'} dangerouslyHtml={props.selectedArticle.description} />
                            </TextComponent>
                        </div>
                    )}

                    {props.selectedArticle.quote_section && props.selectedArticle.quote_section.length > 0 && (
                        <QuoteSectionV2 data={props.selectedArticle.quote_section} />
                    )}

                    {props.selectedArticle.description2 && (
                        <div className={styles.description}>
                            <TextComponent>
                                <PromoDangerouslyHtml color={'baseColor'} tag={'div'} dangerouslyHtml={props.selectedArticle.description2} />
                            </TextComponent>
                        </div>
                    )}

                    {props.selectedArticle.author && (
                        <PromoRow textAlign={'center'}>
                            <div className={styles.authorImgContainer}>
                                <img
                                    className={styles.authorImg}
                                    src={props.selectedArticle.author.authorImg.url}
                                    alt={props.selectedArticle.author.authorName}
                                />
                            </div>
                            <PromoRow indent="8px">
                                <PromoText color={'gray80'} tag={'div'}>
                                    {props.selectedArticle.author.authorName}
                                </PromoText>
                            </PromoRow>
                            <PromoText color={'gray80'} variant={'note'} tag={'div'}>
                                {props.selectedArticle.author.authorRole}
                            </PromoText>
                        </PromoRow>
                    )}

                    {props.selectedArticle.tiles_section && props.selectedArticle.tiles_section.length > 0 && (
                        <TilesSection data={props.selectedArticle.tiles_section} />
                    )}

                    {props.landing.latestPosts && <HeadingSection title={props.landing.latestPosts} />}

                    {props.articles.data && (
                        <div className={styles.blogTiles}>
                            <PromoRow indent="adaptive-32-48-64">
                                {Array.isArray(props.articles.data) &&
                                    props.articles.data
                                        .filter((item, index) => item !== null && index < 2)
                                        .map((item) => {
                                            return (
                                                <BlogTile
                                                    key={item.id}
                                                    date={item.date}
                                                    location={item.location}
                                                    text={item.preview_text}
                                                    categories={item.categories}
                                                    title={item.title}
                                                    img={item.img}
                                                    link={`${url}/${item.slug}`}
                                                />
                                            );
                                        })}
                            </PromoRow>
                        </div>
                    )}
                    {props.landing.backToTopBtn && (
                        <PromoRow indent="adaptive-32-48-64">
                            <BackToTopButton caption={props.landing.backToTopBtn} />
                        </PromoRow>
                    )}
                </PromoSectionContainer>
            )}
        </PromoPageContainer>
    );
};
export default NewsLayoutV2(NewsArticlePageV2);
